import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactModal from 'react-modal';
import InputMask from 'react-input-mask';
import { TbSearch } from 'react-icons/tb';
import { RiCloseLine, RiSearchLine } from 'react-icons/ri';

import { useAuth } from 'context/AuthContext';
import { CAMPO_DA_ACAO, ConfirmacaoDaAcao } from 'components/task-item/modal-actions/moldura-da-acao';
import { UserAvatar } from 'components/user-avatar';
import { findGestor, findManagers, updateUserByIdProfile } from 'services/profile.service';

import { User, UserInfo } from 'types/user';

import FeedbackImg from 'assets/img/comportamento/bg-feedback-moedas.old.png';
import { getPlayerInfo } from 'services/dashboard.service';
import Button from 'components/ui/button';
import { IoIosClose } from 'react-icons/io';
import { AiOutlineEdit } from 'react-icons/ai';
import { FOCO_VISIVEL } from 'utils/foco';

const maskTemplate = 'F9999999';

interface IProps {
  closeModal: () => void;
  onSubmit: (item: any) => void;
  player: any;
  // Oculta gestores cod_etapa 1/2/3 com dupla de liderança na busca (param do aggregate
  // gestores). Ligado apenas pela apreciação de realizações destaque (fluxo normal);
  // os demais usos (ApreciarAcao) mantêm o comportamento atual.
  hideDupla?: boolean;
}

export default function SelectGestor({ closeModal, player, onSubmit, hideDupla = false }: IProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const searchRef = useRef<any>();

  const [isOpen, setIsOpen] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [userEdit, setUserEdit] = useState<UserInfo>();
  const [target, setTarget] = useState<User[]>();
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(0);
  const [mask, setMask] = useState('');
  const [initialManager, setInitialManager] = useState<any>();
  const [searchManager, setSearchManager] = useState<string>('');
  const campoId = useId();
  const avisoId = useId();

  ReactModal.setAppElement('#modal');

  function handleChange() {
    if (searchRef.current) {
      const value = searchRef.current.value.toUpperCase();
      if (value.length > 1) {
        if (value[0].toUpperCase() === 'F' && !isNaN(value[1])) {
          setMask(maskTemplate);
        } else {
          setMask('');
          if (value === '_______') {
            searchRef.current.value = '';
          }
        }
      } else {
        setMask('');
      }
    }
  }

  async function handleSearchManager(e?: any) {
    if (e) {
      e.preventDefault();
    }

    try {
      setSearching(true);

      const result = await findManagers(searchManager, player.cod, true, false, hideDupla);

      const filteredResults = result.filter((item: any) => item._id !== user?._id);

      setSearchResults(filteredResults);

      setSearching(false);
    } catch (e) {
      setSearching(false);
    }
  }

  function removeTarget(index: number) {
    if (target && target.length) {
      const newTargetList = [...target];
      newTargetList.splice(index, 1);
      setTarget(newTargetList);
    }
  }

  function addTarget(item: User) {
    setTarget([item]);
    setSearchResults([]);
    setSearching(false);

    if (searchRef && searchRef.current) {
      searchRef.current.value = '';
    }
  }

  function reset() {
    setTarget(undefined);
    setSubmitted(false);
    setLoading(false);
    setSearchResults([]);
    setSearching(false);
    setStep(0);
    setUserEdit(undefined);
  }

  function handleCloseModal() {
    reset();
    closeModal();
  }

  // O X, o Esc e o "Voltar": com resultados na tela, só limpam a busca; senão fecham o modal.
  function fechar() {
    if (loading) return;

    if (searchResults.length > 0) {
      reset();
    } else {
      setIsOpen(false);
      handleCloseModal();
    }
  }

  useEffect(() => {
    async function getUser() {
      const resp = await getPlayerInfo('F' + player?.gestor?.padStart(7, '0'));
      if (resp) {
        setInitialManager(resp);
      }
    }

    if (player?.gestor) {
      getUser();
    }
  }, [player]);

  function isSwitchable() {
    if (!!initialManager?._id) return true;

    return false;
  }

  return (
    <ConfirmacaoDaAcao
      isOpen={isOpen}
      title="Confirme o gestor da próxima etapa"
      size="md"
      onClose={fechar}
      onConfirm={() => {
        onSubmit(initialManager);
      }}
      confirmLabel="Confirmar gestor"
      confirmDisabled={!isSwitchable()}
      backDisabled={loading}
    >
      {!loading && !submitted && step === 0 && (
        <>
          <div>
            <label htmlFor={campoId} className="mb-1.5 block text-jj-label font-semibold text-content-soft">
              Responsável por apreciar a realização
            </label>

            <div className="flex items-center gap-2">
              <form onSubmit={handleSearchManager} className="flex-1">
                <input
                  id={campoId}
                  value={
                    !!initialManager
                      ? `${initialManager.nome_guerra} (${initialManager.chave}) - ${initialManager.funcao}`
                      : searchManager
                  }
                  disabled={!!initialManager}
                  onChange={e => setSearchManager(e.target.value)}
                  aria-describedby={avisoId}
                  className={`${CAMPO_DA_ACAO} disabled:bg-surface-100`}
                />
              </form>

              {!!initialManager ? (
                <button
                  type="button"
                  onClick={() => setInitialManager(null)}
                  aria-label="Alterar gestor"
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-jj-md bg-brand-yellow-light text-brand-blue-dark ${FOCO_VISIVEL}`}
                >
                  <AiOutlineEdit size={20} aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSearchManager()}
                  aria-label="Buscar gestor"
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-jj-md bg-brand-yellow-light text-brand-blue-dark ${FOCO_VISIVEL}`}
                >
                  <RiSearchLine size={20} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <p id={avisoId} className="m-0 text-xs leading-normal text-brand-blue [text-wrap:pretty]">
            Essa pessoa é responsável por realizar a apreciação desta entrega? Caso não seja ela, você pode alterá-la, clicando no
            ícone acima.
            <br />
            <br />
            Atenção: Não deve ser indicado novo líder nas situações em que o seu gestor tenha ausência temporária (férias, abonos,
            licença paternidade, folgas, viagem a serviço, licença saúde até 15 (quinze) dias etc.).
          </p>

          {target && target?.length > 0 && !searching && (
            <ul className="m-0 flex list-none flex-col gap-3 rounded-jj-md bg-surface-100 p-4">
              {target.map((item: User, index: number) => (
                <li key={item._id} className="flex items-center gap-3">
                  <div className="h-11 w-11 shrink-0">
                    <UserAvatar
                      data={{
                        image: item.image,
                        ...item,
                      }}
                    />
                  </div>

                  <p className="m-0 flex-1 text-xs text-content">
                    <strong className="block text-sm text-brand-blue-dark">{item.nome_guerra}</strong>
                    <strong className="text-sm text-brand-blue-dark">{item.nome}</strong> ({item._id})
                    {!!item?.diretoria || !!item?.funcao ? (
                      <span className="block">
                        {!!item?.diretoria && <span className="uppercase text-brand-blue-dark">{item?.diretoria}</span>}
                        {!!item?.diretoria && !!item?.funcao && ' - '}
                        {item?.funcao ? `${item?.funcao}` : ''}
                      </span>
                    ) : null}
                  </p>

                  <button
                    type="button"
                    onClick={() => removeTarget(index)}
                    aria-label={`Remover ${item.nome}`}
                    className={`flex h-11 w-11 items-center justify-center rounded-full text-brand-blue ${FOCO_VISIVEL}`}
                  >
                    <RiCloseLine size={24} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {searching && (
            <p role="status" className="m-0 animate-pulse text-brand-blue motion-reduce:animate-none">
              Recuperando informações...
            </p>
          )}

          {!searching && !!searchResults?.length && (
            <div className="flex flex-col gap-3 rounded-jj-md bg-surface-100 p-4">
              <ul className="m-0 flex max-h-[30vh] list-none flex-col gap-2 overflow-y-auto p-0 lg:max-h-none lg:overflow-y-visible">
                {searchResults.map(item => (
                  <li key={item._id}>
                    <button
                      type="button"
                      onClick={() => {
                        setInitialManager(item);
                        setSearchResults([]);
                        setSearchManager('');
                      }}
                      className={`flex w-full items-center gap-3 rounded-jj-md bg-brand-white px-3 py-2.5 text-left hover:bg-brand-yellow-light ${FOCO_VISIVEL}`}
                    >
                      <span className="h-11 w-11 shrink-0">
                        <UserAvatar
                          data={{
                            image: item.image,
                            ...item,
                          }}
                        />
                      </span>
                      <span className="flex-1 text-xs text-content">
                        <strong className="text-sm text-brand-blue-dark">{item.nome}</strong> ({item._id})
                        {!!item?.diretoria || !!item?.funcao ? (
                          <span className="block">
                            {!!item?.diretoria && <span className="uppercase text-brand-blue-dark">{item?.diretoria}</span>}
                            {!!item?.diretoria && !!item?.funcao && ' - '}
                            {item?.funcao ? `${item?.funcao}` : ''}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <p className="m-0 text-center text-xs text-content-soft">
                Não encontrou quem procura? Experimente buscar por nome e sobrenome ou matrícula.
              </p>
            </div>
          )}

          {!isSwitchable() && !!target?.length && (
            <p className="m-0 text-center font-semibold text-brand-blue-dark">
              O novo gestor a ser indicado deve pertencer ao nível hierárquico superior ao do funcionário em questão.
            </p>
          )}
        </>
      )}

      {loading && !submitted && (
        <p role="status" className="m-0 animate-pulse text-center text-xl text-brand-blue motion-reduce:animate-none">
          Mudando gestor...
        </p>
      )}
    </ConfirmacaoDaAcao>
  );
}
